export const constructionStageIds = [
  'survey',
  'foundation',
  'structure',
  'finishing',
  'activation',
] as const;

export type ConstructionStageId = (typeof constructionStageIds)[number];

export interface ConstructionStageResolution {
  progress: number;
  stage: ConstructionStageId;
  stageProgress: number;
}

export interface AuthoritativeConstructionWindow {
  startedAt: string | number | Date;
  completesAt: string | number | Date;
  now: string | number | Date;
}

export interface ConstructionMotionProfile {
  animateActivation: boolean;
  animateWorkers: boolean;
  activationGlowAlpha: number;
  workerTravel: number;
}

const stageBoundaries = [0, 0.25, 0.5, 0.75, 1] as const;

function timestamp(value: string | number | Date): number {
  return value instanceof Date
    ? value.getTime()
    : typeof value === 'number'
      ? value
      : Date.parse(value);
}

export function clampConstructionProgress(progress: number): number {
  if (!Number.isFinite(progress)) return 0;
  return Math.min(1, Math.max(0, progress));
}

export function constructionProgressFromTimestamps({
  startedAt,
  completesAt,
  now,
}: AuthoritativeConstructionWindow): number {
  const started = timestamp(startedAt);
  const completes = timestamp(completesAt);
  const current = timestamp(now);
  if (![started, completes, current].every(Number.isFinite) || completes <= started) return 0;
  return clampConstructionProgress((current - started) / (completes - started));
}

export function resolveConstructionStage(progress: number): ConstructionStageResolution {
  const normalized = clampConstructionProgress(progress);
  const stageIndex =
    normalized >= 1
      ? 4
      : normalized >= 0.75
        ? 3
        : normalized >= 0.5
          ? 2
          : normalized >= 0.25
            ? 1
            : 0;
  const stage = constructionStageIds[stageIndex];
  const start = stageBoundaries[stageIndex];
  const end = stageBoundaries[Math.min(stageIndex + 1, stageBoundaries.length - 1)] ?? 1;
  return {
    progress: normalized,
    stage,
    stageProgress: stageIndex === 4 || end === start ? 1 : (normalized - start) / (end - start),
  };
}

export function resolveConstructionStageFromTimestamps(
  window: AuthoritativeConstructionWindow,
): ConstructionStageResolution {
  return resolveConstructionStage(constructionProgressFromTimestamps(window));
}

export function constructionMotionProfile(reducedMotion: boolean): ConstructionMotionProfile {
  return reducedMotion
    ? {
        animateActivation: false,
        animateWorkers: false,
        activationGlowAlpha: 0.18,
        workerTravel: 0,
      }
    : {
        animateActivation: true,
        animateWorkers: true,
        activationGlowAlpha: 0.12,
        workerTravel: 3,
      };
}
